import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosList, IosListItem, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const formatDate = (v) => (v ? String(v).slice(0, 10) : "—");

const formatMoney = (value) => {
  const n = value == null ? 0 : Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

export default function PurchaseOrdersMobile() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => setPage(1), [search, status]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["m-purchase-orders", search, status, page, pageSize],
    queryFn: () =>
      get("/api/purchase-orders", {
        publicationState: "live",
        sort: "updatedAt:desc",
        pagination: { page, pageSize },
        populate: { vendor: true },
        filters: {
          ...(status !== "all" && { status: { $eq: status } }),
          ...(search && {
            $or: [{ purchaseOrderNumber: { $containsi: search } }],
          }),
        },
      }),
    staleTime: 10_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const total = data?.meta?.pagination?.total;

  return (
    <IosPage>
      <MobileTopBar
        title="Purchase Orders"
        subtitle={typeof total === "number" ? `${total} total • page ${page}/${pageCount}` : isFetching ? "Syncing…" : "List"}
        onBack={() => navigate("/m/dashboard")}
      />

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          placeholder="Search PO number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {["all", "draft", "open", "received", "cancelled"].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isLoading ? <LinearProgress sx={{ mt: 1 }} /> : null}
      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Failed to load purchase orders.
        </Typography>
      ) : null}

      <IosList sx={{ mt: 1 }}>
        {rows.map((po) => {
          const documentId = po.documentId;
          const vRaw = po?.vendor?.data || po?.vendor || null;
          const vendor = vRaw ? normalize(vRaw) : null;
          const vendorName = vendor?.name || "";
          const totalMoney = po.totalWithTax != null ? formatMoney(po.totalWithTax) : formatMoney(po.total);
          return (
            <IosListItem
              key={documentId || po.id}
              icon="receipt_long"
              title={po.purchaseOrderNumber || "Purchase order"}
              meta={[vendorName, po.expectedDeliveryDate ? `ETA ${formatDate(po.expectedDeliveryDate)}` : null].filter(Boolean).join(" • ")}
              trailing={totalMoney}
              onClick={() => {
                if (documentId) navigate(`/m/purchase-orders/${documentId}`);
              }}
            />
          );
        })}

        {!isLoading && !rows.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No purchase orders found.
          </Typography>
        ) : null}
      </IosList>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination size="small" count={pageCount} page={page} onChange={(e, p) => setPage(p)} />
        </Box>
      ) : null}
    </IosPage>
  );
}
